/**
 * Host-aware FusionMSI / DecisionPoint routing.
 *
 * On fusionmsi.com the product is served at clean root paths (/auth, /display, …).
 * On app domains (caremap2020.web.app, localhost, …) the same pages live under /dp-*.
 *
 * localStorage FUSION_MSI_HOST=1 previews fusionmsi.com routing on any host.
 * Prompt 06: getOnTheMapPath mirrors the sponsor helper. It is not a nav link.
 */

import { getGeoCity, getGeoCountry } from './geoMarketingData';

export const MARKETING_SLUGS = ['how-it-works', 'capabilities', 'access'];
export const PLATFORM_SLUGS = ['how-it-works', 'for-executives', 'get-started'];

/** Audience marketing pages reachable from the landing gate. */
export const AUDIENCE_SLUGS = ['patient', 'clinician', 'facility', 'fde'];

/**
 * Maps a marketing audience to the account role used by the main-app signup
 * (`AuthPage` reads `?role=patient|provider|facility`). FDEs are clinicians /
 * technical staff, so they onboard as a provider account.
 */
export const AUDIENCE_SIGNUP_ROLE = {
  patient: 'patient',
  clinician: 'provider',
  facility: 'facility',
  fde: 'provider',
};

const MARKETING_BASE = '/dp';
const HOST_PREVIEW_KEY = 'FUSION_MSI_HOST';

export function isFusionMsiHost() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem(HOST_PREVIEW_KEY) === '1') return true;
  } catch {
    // localStorage may be unavailable (private mode).
  }
  return isExactFusionMsiHost(window.location.hostname);
}

/** Apex host only. A product subdomain such as chartmind.fusionmsi.com is not this host (D-VR3-04). */
export function isExactFusionMsiHost(hostname) {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  return host === 'fusionmsi.com' || host === 'www.fusionmsi.com';
}

export function marketingHubPath() {
  return isFusionMsiHost() ? '/' : MARKETING_BASE;
}

/**
 * True on the audience gate — `/` (fusionmsi.com), `/dp` (app hosts), or the
 * `/splashpage` copy of that gate.
 */
export function isFusionMsiMarketingHubPath(pathname = '') {
  if (typeof pathname !== 'string') return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/splashpage') return true;
  if (isFusionMsiHost()) return normalized === '/';
  return normalized === MARKETING_BASE;
}

export function marketingPagePath(slug) {
  if (!slug) return marketingHubPath();
  return isFusionMsiHost() ? `/${slug}` : `${MARKETING_BASE}/${slug}`;
}

/** Path to the About Us page. */
export function aboutPath() {
  return isFusionMsiHost() ? '/about' : `${MARKETING_BASE}/about`;
}

/** Path to the company Business Model page. */
export function businessModelPath() {
  return isFusionMsiHost() ? '/business-model' : `${MARKETING_BASE}/business-model`;
}

/** Public page for the BTYB Marketer / CareStart city sponsor. */
export const CARESTART_SPONSOR_SLUG = 'carestart-sponsor';

export function careStartSponsorPath() {
  return isFusionMsiHost()
    ? `/${CARESTART_SPONSOR_SLUG}`
    : `${MARKETING_BASE}/${CARESTART_SPONSOR_SLUG}`;
}

/**
 * Public CareMap advertising page. Host-aware like the sponsor page.
 * Not a nav or hub link (prompt 06). The md3c host does not serve this path.
 */
export const GET_ON_THE_MAP_SLUG = 'get-on-the-map';

export function getOnTheMapPath() {
  return isFusionMsiHost()
    ? `/${GET_ON_THE_MAP_SLUG}`
    : `${MARKETING_BASE}/${GET_ON_THE_MAP_SLUG}`;
}

/** Path to the company Historical Precedent page. */
export function historicalPrecedentPath() {
  return isFusionMsiHost() ? '/historical-precedent' : `${MARKETING_BASE}/historical-precedent`;
}

/** Path to an audience marketing page (patient, clinician, facility, fde). */
export function audiencePagePath(slug) {
  if (!slug) return marketingHubPath();
  return isFusionMsiHost() ? `/${slug}` : `${MARKETING_BASE}/${slug}`;
}

/**
 * Role-typed signup destination for an audience CTA.
 *
 * On fusionmsi.com, `/auth` is the invite-only DecisionPoint sign-in, so the
 * role-based main-app registration is mounted at `/signup` instead. On app
 * hosts the canonical `/auth?role=` registration is used directly.
 */
export function audienceSignupPath(audience) {
  const role = AUDIENCE_SIGNUP_ROLE[audience];
  const base = isFusionMsiHost() ? '/signup' : '/auth';
  return role ? `${base}?role=${encodeURIComponent(role)}` : base;
}

export function authPath({ next } = {}) {
  const base = isFusionMsiHost() ? '/auth' : '/dp-auth';
  if (!next) return base;
  return `${base}?next=${encodeURIComponent(next)}`;
}

export function joinPath(code) {
  const base = isFusionMsiHost() ? '/join' : '/dp-join';
  if (!code) return base;
  return `${base}?code=${encodeURIComponent(code)}`;
}

export function hierarchyJoinPath(inviteCode) {
  const base = isFusionMsiHost() ? '/join' : '/dp-join';
  if (!inviteCode) return base;
  return `${base}?invite=${encodeURIComponent(inviteCode)}`;
}

export function platformHubPath() {
  return isFusionMsiHost() ? '/platform' : '/dp/platform';
}

export function platformPagePath(slug) {
  if (!slug) return platformHubPath();
  return isFusionMsiHost() ? `/platform/${slug}` : `/dp/platform/${slug}`;
}

/** Path to a country-level geo marketing page (e.g. /kenya or /dp/kenya). */
export function geoCountryPath(countrySlug) {
  if (!countrySlug) return marketingHubPath();
  return isFusionMsiHost() ? `/${countrySlug}` : `${MARKETING_BASE}/${countrySlug}`;
}

/** Path to a city-level geo marketing page (e.g. /kenya/nairobi). */
export function geoCityPath(countrySlug, citySlug) {
  if (!countrySlug || !citySlug) return geoCountryPath(countrySlug);
  return isFusionMsiHost()
    ? `/${countrySlug}/${citySlug}`
    : `${MARKETING_BASE}/${countrySlug}/${citySlug}`;
}

/**
 * True when pathname is a configured geo marketing route (/kenya, /kenya/nairobi, /dp/kenya/…).
 * @param {string} pathname
 */
export function isGeoMarketingPath(pathname = '') {
  if (typeof pathname !== 'string' || !pathname) return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';

  let parts;
  if (isFusionMsiHost()) {
    parts = normalized.replace(/^\/+/, '').split('/').filter(Boolean);
  } else if (normalized.startsWith(`${MARKETING_BASE}/`)) {
    parts = normalized.slice(MARKETING_BASE.length + 1).split('/').filter(Boolean);
  } else {
    return false;
  }

  if (parts.length === 0 || parts.length > 2) return false;
  if (parts.length === 1) return Boolean(getGeoCountry(parts[0]));
  return Boolean(getGeoCity(parts[0], parts[1]));
}

/**
 * Marketing pages an unsigned visitor may open on fusionmsi.com.
 * The caller has already confirmed the host. Product paths such as /display,
 * /admin, and /dashboard are not included.
 * @param {string} pathname
 * @returns {boolean}
 */
export function isFusionMsiPublicMarketingPath(pathname = '') {
  if (typeof pathname !== 'string' || !pathname) return false;
  const normalized = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  if (
    normalized === '/' ||
    normalized === '/splashpage' ||
    normalized === '/flash13' ||
    normalized === '/dp' ||
    normalized === '/platform' ||
    normalized === '/for'
  ) {
    return true;
  }
  if (normalized.startsWith('/for/')) return true;

  const parts = normalized.replace(/^\/+/, '').split('/').filter(Boolean);
  if (parts.length === 1) {
    return isFusionMsiMarketingSegment(parts[0]) || Boolean(getGeoCountry(parts[0]));
  }
  if (parts.length === 2 && parts[0] === 'platform') {
    return PLATFORM_SLUGS.includes(parts[1]);
  }
  if (parts.length === 2 && parts[0] === 'dp') {
    return (
      isFusionMsiMarketingSegment(parts[1]) ||
      parts[1] === 'platform' ||
      Boolean(getGeoCountry(parts[1]))
    );
  }
  if (parts.length === 2) return Boolean(getGeoCity(parts[0], parts[1]));
  if (parts.length === 3 && parts[0] === 'dp' && parts[1] === 'platform') {
    return PLATFORM_SLUGS.includes(parts[2]);
  }
  if (parts.length === 3 && parts[0] === 'dp') {
    return Boolean(getGeoCity(parts[1], parts[2]));
  }
  return false;
}

function isFusionMsiMarketingSegment(segment) {
  return (
    MARKETING_SLUGS.includes(segment) ||
    AUDIENCE_SLUGS.includes(segment) ||
    segment === 'about' ||
    segment === 'business-model' ||
    segment === 'historical-precedent' ||
    segment === CARESTART_SPONSOR_SLUG ||
    segment === GET_ON_THE_MAP_SLUG
  );
}

export function isFusionMsiBaseMarketingPath(pathname = '') {
  if (typeof pathname !== 'string' || !pathname) return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/splashpage') return true;

  if (isFusionMsiHost()) {
    if (normalized === '/') return true;
    const parts = normalized.replace(/^\/+/, '').split('/').filter(Boolean);
    if (parts.length === 1) {
      const segment = parts[0];
      return (
        MARKETING_SLUGS.includes(segment) ||
        AUDIENCE_SLUGS.includes(segment) ||
        segment === 'about' ||
        segment === 'business-model' ||
        segment === 'historical-precedent' ||
        segment === CARESTART_SPONSOR_SLUG ||
        segment === GET_ON_THE_MAP_SLUG ||
        segment === 'platform'
      );
    }
    if (parts.length === 2 && parts[0] === 'platform') {
      return PLATFORM_SLUGS.includes(parts[1]);
    }
    return false;
  }

  if (normalized === MARKETING_BASE) return true;
  if (normalized === `${MARKETING_BASE}/about`) return true;
  if (normalized === `${MARKETING_BASE}/business-model`) return true;
  if (normalized === `${MARKETING_BASE}/historical-precedent`) return true;
  if (normalized === `${MARKETING_BASE}/${CARESTART_SPONSOR_SLUG}`) return true;
  if (normalized === `${MARKETING_BASE}/${GET_ON_THE_MAP_SLUG}`) return true;
  if (normalized.startsWith(`${MARKETING_BASE}/platform`)) return true;

  const dpSegment = normalized.replace(/^\/dp\/?/, '').split('/')[0];
  if (
    MARKETING_SLUGS.includes(dpSegment) ||
    AUDIENCE_SLUGS.includes(dpSegment)
  ) {
    return true;
  }
  return false;
}

export function displayPath() {
  return isFusionMsiHost() ? '/display' : '/dp-display';
}

export function adminPath(subpath = '') {
  const prefix = isFusionMsiHost() ? '/admin' : '/dp-admin';
  if (!subpath) return prefix;
  const normalized = subpath.startsWith('/') ? subpath : `/${subpath}`;
  return `${prefix}${normalized}`;
}

export function reviewPath() {
  return isFusionMsiHost() ? '/review' : '/dp-review';
}

export function contentSourcesPath() {
  return isFusionMsiHost() ? '/sources' : '/dp-sources';
}

export function libraryPath() {
  return adminPath('library');
}

export function recordEditorPath(type, recordId) {
  return adminPath(`records/${type}/${recordId}`);
}

/** Paths that show the admin top-bar link cluster (bedside, import, review). */
export function adminTopBarPrefixes() {
  return isFusionMsiHost()
    ? ['/admin', '/review', '/sources']
    : ['/dp-admin', '/dp-review', '/dp-sources'];
}

export function isAdminTopBarPath(pathname = '') {
  return adminTopBarPrefixes().some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isJoinReturnPath(pathname = '') {
  return pathname.startsWith('/dp-join') || pathname.startsWith('/join');
}

/** True when the FusionMSI favicon/branding should apply for the given path. */
export function isFusionMsiBrandedPath(pathname = '') {
  if (typeof pathname !== 'string') return false;
  if (isFusionMsiHost()) return true;
  return (
    pathname === '/flash13' ||
    pathname === `${MARKETING_BASE}/flash13` ||
    pathname === '/splashpage' ||
    pathname === MARKETING_BASE ||
    pathname.startsWith(`${MARKETING_BASE}/`) ||
    pathname.startsWith('/dp-')
  );
}
